import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../redux/app';
import { setSymbolInfo } from '../../../redux/reducer/instruments';
import { setPayload } from '../../../redux/reducer/payload';
import { checkMinMaxVolume } from '../../../utilities/instrumentInfo';
import { openPosition } from '../../../api/trade';
import { Notification } from '../../../utilities/alert';
import { typeExec } from '../components/TradeFunction';
import moment from 'moment';
import { calcValume, MTProfileCalculator } from '@vt/vt-trading-utils';
import useAuth from '../../../hooks/useAuth';
import EddDialog from '../../dialog/EddDialog';
import { Edd } from '../../../data/edd';
import { getUserVerifyUser } from '../../../api/account';
import { ORIGIN } from '../../../constants';
import { makeSymbolKeySelector } from '../../../redux/selector';

const EDD_STATUS = {
  pending: {
    title: 'Submit Enhanced Due Diligence Documents',
    message: 'You may have been identified as a potential Politically Exposed Person (PEP) or high-net-worth individual. Please complete the Enhanced Due Diligence (EDD) verification within 30 days to avoid forced position closures, trading restrictions, and deposit limitations.'
  },
  rejected: (date) => {
    return {
      title: 'EDD Document Rejected',
      message: `Your EDD documents did not pass verification. Please resubmit the required documents by ${date} to proceed.`
    }
  },
  terminated: {
    title: 'Document Rejected',
    message: 'Your documents did not meet verification requirements. Trading is restricted, and open positions will be closed. Please withdraw within 14 days, the client area will be suspended afterward.'
  },
  timeout: {
    title: 'Document Rejected',
    message: 'Your documents did not meet verification requirements. Trading is restricted, and open positions will be closed. Please withdraw within 14 days, the client area will be suspended afterward.'
  },
};

const getOriginUrl = (countryId, originUrl) => {
  if (countryId === 9) {
    return 'https://www.id-iux.com';
  } else if (countryId === 10) {
    return 'https://www.iuxvn.com';
  } else {
    return ORIGIN[originUrl];
  }
};

export const TradeContext = createContext('');

const isDst = () => {
  return moment.tz('Europe/London').isDST();
};

const edd = new Edd();

const TradeContextProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user, getFirebaseCustom } = useAuth();
  const instrumentInfo = useAppSelector((state) => state.symbol?.instrumentInfo);
  const payload = useAppSelector((state) => state.payload?.payload);
  const priceTrigerRef = useRef(null);
  const { active } = useSelector(({ symbol }) => symbol);
  const priceRef = useRef(null);
  const lotRef = useRef(null);
  const [pre, setPre] = useState(active);
  const [changeFunc, setChangeFunc] = useState(false);
  const [lotSize, setLotSize] = useState(0.01);
  const [eddData, setEddData] = useState(null);
  const symbolKey = useAppSelector(useMemo(makeSymbolKeySelector, []));

  const calculator = new MTProfileCalculator(symbolKey);

  const [openAnnouncement, setOpenAnnouncement] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    edd_status: null
  });

  useEffect(() => {
    if (instrumentInfo) {
      dispatch(
        setPayload({
          ...payload,
          action: 200,
          digits: 0,
          price_sl: 0,
          price_tp: 0,
          symbol: instrumentInfo?.symbol,
          type: 0,
          type_fill: 0,
          volume: 100,
          price_order: 0,
          price_trigger: 0,
          type_time: 0,
          account_trade_id: localStorage.getItem('iuxmarkets.active.account')
        })
      );
      setLotSize(instrumentInfo?.volume_step / 10000);
    }
  }, [instrumentInfo]);

  useEffect(() => {
    if (active !== pre || changeFunc) {
      const t = moment.tz('Europe/London').isDST()
        ? moment.utc().unix() + 3600
        : moment.utc().unix();

      setPre(active);
      setChangeFunc(false);
      dispatch(
        setPayload({
          ...payload,
          action: 200,
          digits: 0,
          price_sl: 0,
          price_tp: 0,
          symbol: null,
          type: 0,
          type_fill: 0,
          volume: 100,
          price_order: 0,
          price_trigger: 0,
          account_trade_id: localStorage.getItem('iuxmarkets.active.account')
        })
      );
    }
    dispatch(setSymbolInfo(active));
  }, [active, changeFunc]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await edd.initialize();
        setEddData(data);
      } catch (error) {
        console.error('Failed to initialize EDD data:', error);
      }
    })()
  }, [])


  const handleLotSize = async (params) => {
    try {
      const data = await calcValume({
        lotSize: +lotSize,
        type: params,
        volume_max: instrumentInfo?.volume_max,
        volume_step: instrumentInfo?.volume_step
      });
      setLotSize(data);
    } catch (error) {
      console.error('Error adjusting volume:', error);
      throw error;
    }
  };

  const inputLotSize = (value) => {
    if (value === '') {
      setLotSize(value);
    } else {
      let regx = new RegExp(/^\d*\.?\d*$/);
      let val = value;
      if (!regx.test(value)) {
        return;
      } else {
        if (val.toString().split('.')[1]?.length >= 2) {
          setLotSize((+val).toFixed(2));
        } else {
          setLotSize(val);
        }
      }
    }
  };

  const validateLotSize = (value) => {
    if (checkMinMaxVolume('min', +value, instrumentInfo?.volume_min).con) {
      setLotSize(checkMinMaxVolume('min', +value, instrumentInfo?.volume_min).value);
    }
    if (checkMinMaxVolume('max', +value, instrumentInfo?.volume_max).con) {
      setLotSize(checkMinMaxVolume('max', +value, instrumentInfo?.volume_max).value);
    }
  };

  const handleExecute = async (type, action, fill, price) => {
    const isVerified = await isUserVerified();
    if (!isVerified.status) {
      setOpenAnnouncement({
        title: isVerified.edd_status === 'rejected' ? EDD_STATUS[isVerified.edd_status](isVerified.rejected_date).title : EDD_STATUS[isVerified.edd_status]?.title ?? 'EDD is required',
        message: isVerified.edd_status === 'rejected' ? EDD_STATUS[isVerified.edd_status](isVerified.rejected_date).message : EDD_STATUS[isVerified.edd_status]?.message ?? 'EDD is required',
        open: true,
        type: 'warning',
        edd_status: isVerified.edd_status
      });
      return;
    }

    try {
      const verifyUser = await getUserVerifyUser();
      if (verifyUser?.data?.status === 'terminated' && verifyUser?.data?.type === 'sumsub') {
        setOpenAnnouncement({
          title: 'Notice of Account Suspension',
          message: 'Your documents did not meet verification requirements. Trading is restricted, and open positions will be closed. Please withdraw your funds; the client area will be suspended afterward.',
          open: true,
          type: 'warning',
          edd_status: 'final_rejected'
        });
        return;
      }
    } catch (error) {
      console.error('Failed to verify user:', error);
    }

    try {
      const tradePayload = buildTradePayload(type, action, fill, price);
      const { data } = await openPosition(tradePayload);

      // Update state and notify user of success
      dispatch(setPayload({ ...payload, time_expiration: payload.time_expiration }));
      Notification({
        title: typeExec[type],
        message: data.data.message,
        type: 'success',
        backgroundColor: '#FFFFFF'
      });
    } catch ({ response }) {
      if (response?.data?.code === 4007) {
        Notification({
          message: response?.data?.error || 'EDD is required',
          type: 'danger'
        });
      } else {
        Notification({
          message: response?.data?.error?.message || 'Trade execution failed',
          type: 'danger'
        });
      }
    }
  };

  const isUserVerified = async () => {
    if (user?.data?.personal_detail?.edd_status) {
      const eddStatus = user?.data?.personal_detail?.edd_status;
      if (eddStatus === 'pending' || eddStatus === 'terminated' || eddStatus === 'rejected') {
        if (eddData && eddData?.rejected_type === 'Resubmit') {
          return {
            status: false,
            type: 'edd',
            edd_status: 'rejected',
            rejected_date: moment(eddData?.end_date).format('DD/MM/YYYY')
          };
        } else {
          return {
            status: false,
            type: 'edd',
            edd_status: eddStatus,
            rejected_date: null
          };
        }
      }
    }
    // pass all
    return {
      status: true,
      type: '',
      edd_status: null,
      rejected_date: moment().format('DD/MM/YYYY')
    };
  };

  const buildTradePayload = (type, action, fill, price) => {
    const timeUnix = isDst() ? moment().unix() + 3600 : moment().unix();
    let timeExpire = payload.type_time === 1 ? timeUnix : payload.time_expiration;
    if (payload.type_time === 3 && payload.time_expiration === 0) {
      timeExpire = moment().format('YYYY-MM-DD');
      timeExpire = moment(`${timeExpire}T00:00:00.000Z`).utc().unix();
    }
    const lotSizeVolume = parseInt((lotSize * 10000).toFixed(0));

    const basePayload = {
      ...payload,
      type,
      action,
      digits: instrumentInfo?.digit,
      volume: lotSizeVolume,
      time_expiration: timeExpire
    };

    // Add price-specific properties
    return price ? { ...basePayload, price } : { ...basePayload, type_fill: fill };
  };

  return (
    <TradeContext.Provider
      value={{
        instrumentInfo,
        handleLotSize,
        inputLotSize,
        validateLotSize,
        handleExecute,
        setLotSize,
        setChangeFunc,
        priceTrigerRef,
        priceRef,
        lotSize,
        lotRef,
        active,
        calculator
      }}>
      {openAnnouncement.open && (
        <EddDialog
          open={openAnnouncement.open}
          title={openAnnouncement.title}
          message={openAnnouncement.message}
          type={openAnnouncement.type}
          edd_status={openAnnouncement.edd_status}
          onCancel={() => setOpenAnnouncement({ ...openAnnouncement, open: false })}
          onSubmit={() => {
            getFirebaseCustom().then((token) => {
              window.open(
                `${getOriginUrl(
                  user?.data?.personal_detail?.country_id,
                  window.location.origin
                )}/en/dashboard/edd/${token}`
              );
              setOpenAnnouncement({ ...openAnnouncement, open: false });
            })
          }}
        />
      )}
      {children}
    </TradeContext.Provider>
  );
};

export default TradeContextProvider;
